import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface NO3ReportFiltersProps {
  customFilters: {
    serviceTypes?: string[];
    mailFormats?: string[];
    accountingTypes?: string[];
    includeIpkSummary?: boolean;
    direction?: string;
    year?: string;
    onServiceTypesChange?: (values: string[]) => void;
    onMailFormatsChange?: (values: string[]) => void;
    onAccountingTypesChange?: (values: string[]) => void;
    onIpkSummaryChange?: (checked: boolean) => void;
    onDirectionChange?: (direction: string) => void;
    onYearChange?: (year: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearOptions?: string[];
  };
}

const NO3ReportFilters: React.FC<NO3ReportFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [serviceTypesAnchorEl, setServiceTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mailFormatsAnchorEl, setMailFormatsAnchorEl] = React.useState<null | HTMLElement>(null);
  const [accountingTypesAnchorEl, setAccountingTypesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [yearAnchorEl, setYearAnchorEl] = React.useState<null | HTMLElement>(null);

  const isServiceTypesMenuOpen = Boolean(serviceTypesAnchorEl);
  const isMailFormatsMenuOpen = Boolean(mailFormatsAnchorEl);
  const isAccountingTypesMenuOpen = Boolean(accountingTypesAnchorEl);
  const isYearMenuOpen = Boolean(yearAnchorEl);

  // Filter options
  const serviceTypeOptions = [
    { id: 'A', label: 'A - Airmail Priority' },
    { id: 'B', label: 'B - SAL Non Priority' },
    { id: 'C', label: 'C - Surface Non-Priority' },
    { id: 'D', label: 'D - Surface Priority' },
  ];

  const mailFormatOptions = [
    { id: 'S', label: 'Format Separated' },
    { id: 'X', label: 'PGE Mixed' },
    { id: 'C', label: 'Postcards Format C' },
  ];

  const accountingTypeOptions = [
    { id: 'G', label: 'Gross' },
    { id: 'N', label: 'Net' },
  ];

  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Generate year options from 2008 to current year
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearOptions = React.useMemo(() => {
    // If parent provides cascaded year options, use them
    if (customFilters.yearOptions && customFilters.yearOptions.length > 0) {
      return customFilters.yearOptions.map(year => ({ value: year, label: year }));
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string }[] = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      options.push({
        value: year.toString(),
        label: year.toString()
      });
    }
    
    return options;
  }, [customFilters.yearOptions]);

  // Handlers
  const handleServiceTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setServiceTypesAnchorEl(event.currentTarget);
  };

  const handleServiceTypesMenuClose = () => {
    setServiceTypesAnchorEl(null);
  };

  const handleMailFormatsClick = (event: React.MouseEvent<HTMLElement>) => {
    setMailFormatsAnchorEl(event.currentTarget);
  };

  const handleMailFormatsMenuClose = () => {
    setMailFormatsAnchorEl(null);
  };

  const handleAccountingTypesClick = (event: React.MouseEvent<HTMLElement>) => {
    setAccountingTypesAnchorEl(event.currentTarget);
  };

  const handleAccountingTypesMenuClose = () => {
    setAccountingTypesAnchorEl(null);
  };

  const handleYearClick = (event: React.MouseEvent<HTMLElement>) => {
    setYearAnchorEl(event.currentTarget);
  };

  const handleYearMenuClose = () => {
    setYearAnchorEl(null);
  };

  const handleYearChange = (year: string) => {
    customFilters.onYearChange?.(year);
    handleYearMenuClose();
  };

  const handleServiceTypeToggle = (value: string) => {
    const currentValues = customFilters.serviceTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onServiceTypesChange?.(newValues);
  };

  const handleMailFormatToggle = (value: string) => {
    const currentValues = customFilters.mailFormats || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onMailFormatsChange?.(newValues);
  };

  const handleAccountingTypeToggle = (value: string) => {
    const currentValues = customFilters.accountingTypes || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    customFilters.onAccountingTypesChange?.(newValues);
  };

  const handleDirectionChange = (direction: string) => {
    customFilters.onDirectionChange?.(direction);
  };

  const handleIpkSummaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    customFilters.onIpkSummaryChange?.(event.target.checked);
  };

  // Display labels
  const getServiceTypesLabel = () => {
    const selected = customFilters.serviceTypes || [];
    if (selected.length === 0) return 'Select Service Types';
    if (selected.length === serviceTypeOptions.length) return 'All Types';
    return `${selected.length} Types`;
  };

  const getMailFormatsLabel = () => {
    const selected = customFilters.mailFormats || [];
    if (selected.length === 0) return 'Select Mail Formats';
    if (selected.length === mailFormatOptions.length) return 'All Formats';
    return `${selected.length} Formats`;
  };

  const getAccountingTypesLabel = () => {
    const selected = customFilters.accountingTypes || [];
    if (selected.length === 0) return 'Select Accounting Types';
    if (selected.length === accountingTypeOptions.length) return 'All Types';
    return `${selected.length} Types`;
  };

  const getYearLabel = () => {
    return customFilters.year || 'Select Year';
  };

  return (
    <>
      {/* Direction Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <StyledSegmentedControl>
          {directionOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={customFilters.direction === option.id}
              onClick={() => handleDirectionChange(option.id)}
              sx={{ 
                color: customFilters.direction === option.id ? '#ffffff' : '#00B6BA',
                minWidth: '70px'
              }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      </Box>

      {/* Year Filter */}
      <FilterButton onClick={handleYearClick}>
        <span className='label'>Year</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getYearLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={yearAnchorEl}
        open={isYearMenuOpen}
        onClose={handleYearMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '120px',
          },
        }}
      >
        <MenuItem onClick={() => handleYearChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleYearChange(option.value)}
            selected={customFilters.year === option.value}
            sx={{
              fontWeight: customFilters.year === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Service Types Filter */}
      <FilterButton onClick={handleServiceTypesClick}>
        <span className='label'>Service Types</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getServiceTypesLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={serviceTypesAnchorEl}
        open={isServiceTypesMenuOpen}
        onClose={handleServiceTypesMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {serviceTypeOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleServiceTypeToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Checkbox
              checked={customFilters.serviceTypes?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Mail Formats Filter */}
      <FilterButton onClick={handleMailFormatsClick}>
        <span className='label'>Mail Formats</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getMailFormatsLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={mailFormatsAnchorEl}
        open={isMailFormatsMenuOpen}
        onClose={handleMailFormatsMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '200px',
          },
        }}
      >
        {mailFormatOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleMailFormatToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Checkbox
              checked={customFilters.mailFormats?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Accounting Types Filter */}
      <FilterButton onClick={handleAccountingTypesClick}>
        <span className='label'>Accounting Types</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getAccountingTypesLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={accountingTypesAnchorEl}
        open={isAccountingTypesMenuOpen}
        onClose={handleAccountingTypesMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '180px',
          },
        }}
      >
        {accountingTypeOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleAccountingTypeToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px',
            }}
          >
            <Checkbox
              checked={customFilters.accountingTypes?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* IPK Summary Checkbox */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
        <Checkbox
          checked={customFilters.includeIpkSummary || false}
          onChange={handleIpkSummaryChange}
          sx={{ padding: '4px' }}
        />
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#063142' }}>
          Include IPK Summary
        </Typography>
      </Box>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('PDF')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'PDF'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('XLS')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'XLS'}
          </Typography>
        </StyledActionButton>
      </Box>
    </>
  );
};

export default NO3ReportFilters; 